import React from 'react'

import { UI2IconName, UI2IconNames } from '../../icons/icon-names'

/**
 * @uiComponent
 * @uiComponentDescription A tone-driven callout box with a title, a plain-text or Markdown body, an
 * icon, and optional action controls below the body.
 *
 * `mode` sets the tone and also picks the default icon; an explicit `icon`
 * overrides that pick, and `markdownText` wins over `text` when both are set.
 *
 * Use cases: Surfacing a success or error message, inline form warnings, informational callouts, wizard completion banners.
 *
 * See the [UI2 Banner Sandbox](https://app.withvector.com/tools/rendering?selectedPlatform=web&selectedComponent=ui2%3Abanner&selectedEditorDisplayMode=tabbed&jsonProps=eJy1Vk1zmzAQ%2FSs7NCePsbHjj8S3psmh07Q9kFvJQUiLrRgkKgnHnoxneuoP6PQX5pd0QBhjTNKmbXRAg%2FZp32rfE8ODg8Jws%2FGN4mLuzJyHQAAEjkLBMF%2FyiWChXAfODB62XRskcfw5yle%2B5O8AD3bKQycKi0jg9Ac9r99M07vTUgSOxW%2Fz6bbMmfA1F7qgsdHAIdTwFdZ4akx53Fb%2Bnlm%2BMQnPKEbExak3dEc48dyQnUfulIzD0BszMp2EO%2BaSG%2BA2n3bHogqJQXaxOajikGU6oGQYnVJ3eDbw3NGQDdwzwsbueBJ603A8OqdsEjjd3WbGdRqTzSeSoN3%2FgXATbwT4UmWJLWcbiK3TdTRdYEKaOpzYZbt5YUw66%2FfzHrp2vSfVvM8UiYzrjfp27U3JHzic%2FV6KEpoJ%2FjXDl%2FXyxW4oWslR9xM0hBFD7MrmGVeUwANFBElQp4SWLT22al3Uv%2FDVkWhXRZWVrG2%2BGJTD9TzPazx2o%2Ba%2B7kuIwS898G%2F8g2P3Vz7lotaUg0thNmlZjAzvkJpK9lTJFFUu50FzWz8ce0c8kc4GGym3NUPsysm4X12IinOfNOPDWSRVUruCuVO4mNu4rkfogsdMoXjaCceJfaSGV%2FemwnET14AhEQJVA9PK1mBsY21JZmGJZCWMi0i2AGpVvU1TyYVJUBigUkRcJcjatuDa2B3XfIUQS8K6cCnpEkZdGIxmpx589G96eycduvn%2FHOeeKFEodoThVAqLobGky7YsRC2ZvBc31UEuFV%2BhAq6h0xmOIeGi04GYGITHbz9hIWMGZoHApFSvfC5GxLwVUhPKRxJDwnVCDF083wGuaIzu%2Bo%2Ba4NO8PAY6T88kahDSQEFSnP7i83WvJdGTnm1pSFtTrJ26bcAVUZwIc3QtG%2BmqEzx%2B%2FwFXmpJCOSOLqhdZCDpLUa24lgpCjKRCyERuWy7mDTlh%2F%2BHbjdvXlFtnlKLWz%2Bv9boF06XIBVCZpjAYbFty%2FVLUe%2FLrk%2Fw7bX3lnuQ4%3D)
 * to test out configurations.
 */
export interface UI2BannerConfigProps {
  /** Visual tone. Also picks a default icon when `icon` is not provided. Defaults to `'neutral'`. */
  mode?: BannerMode
  /** Title rendered at the top of the banner. */
  title?: string
  /** Plain-text body. Ignored when `markdownText` is provided. */
  text?: string
  /** Markdown body. Takes precedence over `text` when both are set. */
  markdownText?: string
  /** Icon override. When omitted, `BANNER_MODE_ICONS` picks one from `mode`. */
  icon?: UI2IconName
  /** Actions/controls rendered inside the banner, below the body. */
  children?: React.ReactNode
}

export type BannerMode = 'success' | 'info' | 'warning' | 'danger' | 'neutral'

/** Role → shape, defined once so every platform draws the same glyph for a mode. */
export const BANNER_MODE_ICONS: Record<BannerMode, UI2IconName> = {
  success: UI2IconNames.CIRCLE_CHECK,
  info: UI2IconNames.INFO,
  warning: UI2IconNames.TRIANGLE_ALERT,
  danger: UI2IconNames.CIRCLE_ALERT,
  neutral: UI2IconNames.INFO,
}

export const UI2_BANNER_DEFAULT_PROPS: Required<Pick<UI2BannerConfigProps, 'mode'>> = {
  mode: 'neutral',
}
